/** Web push in de browser (issue #223): ondersteuning, toestemming en abonnement van dit apparaat. */

export type PushSupport =
  /** Kan: service worker, PushManager en Notification zijn er. */
  | "ok"
  /** iPhone/iPad in Safari: eerst op het beginscherm zetten (iOS 16.4+). */
  | "ios-homescreen"
  /** Browser kan geen push (of private modus). */
  | "unsupported";

const ua = () => (typeof navigator === "undefined" ? "" : navigator.userAgent);

/** iPhone, iPad (ook iPadOS die zich als Mac voordoet). */
export const isIos = () =>
  /iPhone|iPad|iPod/.test(ua()) ||
  (/Macintosh/.test(ua()) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);

/** Geopend vanaf het beginscherm (als app). */
export const isStandalone = () =>
  (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches) ||
  (typeof navigator !== "undefined" && (navigator as Navigator & { standalone?: boolean }).standalone === true);

export function pushSupport(): PushSupport {
  const has =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
  if (has) return "ok";
  if (isIos() && !isStandalone()) return "ios-homescreen";
  return "unsupported";
}

/** Korte naam van dit apparaat voor de lijst, bv. "iPhone · Safari". */
export function deviceLabel(): string {
  const u = ua();
  const device = /iPhone/.test(u)
    ? "iPhone"
    : /iPad/.test(u) || (isIos() && /Macintosh/.test(u))
      ? "iPad"
      : /Android/.test(u)
        ? "Android"
        : /Windows/.test(u)
          ? "Windows"
          : /Macintosh/.test(u)
            ? "Mac"
            : /Linux/.test(u)
              ? "Linux"
              : "Apparaat";
  const browser = /Edg\//.test(u)
    ? "Edge"
    : /Firefox\//.test(u)
      ? "Firefox"
      : /Chrome\//.test(u)
        ? "Chrome"
        : /Safari\//.test(u)
          ? "Safari"
          : null;
  return [device, isStandalone() ? "app" : browser].filter(Boolean).join(" · ");
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration() {
  return navigator.serviceWorker.ready;
}

/** Huidig abonnement van dit apparaat, of null. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "ok") return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/**
 * Toestemming vragen en abonneren. Moet vanuit een klik gebeuren (iOS en Safari eisen dat).
 * Geeft het abonnement terug, of gooit met een Nederlandse foutmelding.
 */
export async function subscribe(publicKey: string): Promise<PushSubscription> {
  const perm = await Notification.requestPermission();
  if (perm !== "granted")
    throw new Error(
      perm === "denied"
        ? "Meldingen zijn geblokkeerd voor Klantpunt. Zet ze aan in de instellingen van je browser of telefoon."
        : "Geen toestemming gegeven voor meldingen.",
    );
  const reg = await registration();
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    // Andere sleutel (bv. nieuwe VAPID-sleutels op de server): opnieuw abonneren.
    const k = existing.options?.applicationServerKey;
    const same = k && btoaUrl(new Uint8Array(k)) === publicKey.replace(/=+$/, "");
    if (same) return existing;
    await existing.unsubscribe();
  }
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
}

function btoaUrl(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Abonnement op dit apparaat opzeggen; geeft het endpoint terug (om de server te laten weten). */
export async function unsubscribe(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  return endpoint;
}
